<template>
  <div class="fin-page">
    <!-- ===================== 顶部工具条 ===================== -->
    <div class="fin-head">
      <div class="fin-title">
        <h2>经营看板</h2>
        <p class="fin-sub">月度收入 / 成本 / 毛利，费用手工录入，电水费按抄表自动计算</p>
      </div>
      <div class="fin-month">
        <button class="fin-btn fin-btn-ghost" @click="shiftMonth(-1)">‹ 上月</button>
        <span class="fin-month-text">{{ month }}</span>
        <button class="fin-btn fin-btn-ghost" @click="shiftMonth(1)">下月 ›</button>
        <button class="fin-btn fin-btn-ghost" :disabled="isCurrentMonth" @click="goCurrentMonth">本月</button>
      </div>
      <div class="fin-actions">
        <button class="fin-btn fin-btn-ghost" @click="openMeterDialog">表具管理</button>
        <button class="fin-btn fin-btn-ghost" @click="openIncomeDialog">+ 其他收入</button>
        <button class="fin-btn" @click="openExpenseDialog(null)">+ 记一笔费用</button>
      </div>
    </div>

    <!-- ===================== 指标卡 ===================== -->
    <div class="fin-cards">
      <div class="fin-card">
        <p class="fin-card-label">订单收入</p>
        <p class="fin-card-value">{{ fmtMoney(income.order) }}</p>
        <p class="fin-card-tip">{{ income.orderCount || 0 }} 单 · 自动汇总</p>
      </div>
      <div class="fin-card">
        <p class="fin-card-label">其他收入</p>
        <p class="fin-card-value">{{ fmtMoney(income.manual) }}</p>
        <p class="fin-card-tip">手工录入 {{ (income.manualItems || []).length }} 笔</p>
      </div>
      <!-- 废料收入（口径③，2026-10-05）：客户剩板**不拉走**，由我方代卖变现。
           废料收入 = 重量 × **当月实际售价**（卖废料**全额**）；
           其中「实赚差价」= 全额 − 折现给客户的钱(Σ$fei) —— 这才是真正赚到的废料钱
           （单 16418：211.12kg × 2.0 − 358.90 = 63.34）。
           点这张卡可配置该月各材质桶的实际售价（碳钢类 / 201 / 304）。 -->
      <div class="fin-card" :title="wasteIncomeTip" style="cursor:pointer" @click="openWastePriceDialog">
        <p class="fin-card-label">废料收入 <span class="fin-muted fin-small">⚙ 改价</span></p>
        <p class="fin-card-value">{{ fmtMoney(income.waste) }}</p>
        <p class="fin-card-tip">剩板变现 · 抵客户加工费</p>
        <p class="fin-card-tip">其中实赚差价 <b>{{ fmtMoney(income.wasteMargin) }}</b></p>
      </div>
      <div class="fin-card fin-card-strong">
        <p class="fin-card-label">总收入</p>
        <p class="fin-card-value">{{ fmtMoney(income.total) }}</p>
        <p class="fin-card-tip">订单 + 其他 + 废料</p>
      </div>
      <div class="fin-card">
        <p class="fin-card-label">直接成本</p>
        <p class="fin-card-value">{{ fmtMoney(cost.direct) }}</p>
        <p class="fin-card-tip">抄表 {{ fmtMoney(cost.directMeter) }} · 手工 {{ fmtMoney(cost.directManual) }}</p>
      </div>
      <div class="fin-card">
        <p class="fin-card-label">期间费用</p>
        <p class="fin-card-value">{{ fmtMoney(cost.period) }}</p>
        <p class="fin-card-tip">房租 / 工资 / 管理费等</p>
      </div>
      <!-- 材料成本：按订单报价记录（realKgPrice / realM2Price）自动复算，口径为**整板采购**
           （剩板回收不算成本减项，它走收入侧的「废料收入」，详见 materialCostTip）。
           ⚠️ 2026-10-04 起它**计入最终净利**（此前只作参考、不计入）。 -->
      <div class="fin-card" :title="materialCostTip">
        <p class="fin-card-label">材料成本</p>
        <p class="fin-card-value">{{ ref.materialCost == null ? '—' : fmtMoney(ref.materialCost) }}</p>
        <p class="fin-card-tip">整板采购 · 计入净利</p>
      </div>
      <!-- 外协成本 = 订单外协（报价明细「其他」列勾了「外协」）+ 手工费用里勾了「外协」的。
           ⚠️ 2026-10-04 起这两部分相加，且都计入最终净利（订单侧以前只是参考值）。 -->
      <div class="fin-card" :title="outsourceCostTip">
        <p class="fin-card-label">外协成本</p>
        <p class="fin-card-value">{{ fmtMoney(cost.outsource) }}</p>
        <p class="fin-card-tip">订单 {{ fmtMoney(cost.outsourceOrder) }} + 手工 {{ fmtMoney(cost.outsourceManual) }}</p>
      </div>
      <div class="fin-card fin-card-strong">
        <p class="fin-card-label">毛利</p>
        <p class="fin-card-value">{{ fmtMoney(profit.gross) }}</p>
        <p class="fin-card-tip">毛利率 {{ fmtRate(profit.grossRate) }}</p>
      </div>
      <!-- 净利 = 总收入 − 直接成本 − 期间费用 − 材料成本 − 订单外协（差额见「最终净利计算过程」） -->
      <div class="fin-card fin-card-strong" :title="netProfitTip">
        <p class="fin-card-label">净利</p>
        <p class="fin-card-value">{{ fmtMoney(profit.net) }}</p>
        <p class="fin-card-tip">净利率 {{ fmtRate(profit.netRate) }} · 已扣材料与外协</p>
      </div>
    </div>

    <!-- ===================== 主体两栏 ===================== -->
    <div class="fin-grid">
      <!-- 左栏：收入 + 成本明细 -->
      <div class="fin-col fin-col-main">
        <!-- 收入明细 -->
        <section class="fin-section">
          <div class="fin-section-head">
            <h3>收入明细</h3>
            <span class="fin-section-total">合计 {{ fmtMoney(income.total) }}</span>
          </div>
          <table class="fin-table">
            <thead>
            <tr>
              <th style="width: 110px">来源</th>
              <th>项目</th>
              <th style="width: 140px" class="fin-t-right">金额</th>
              <th style="width: 90px" class="fin-t-right">操作</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td><span class="fin-tag fin-tag-auto">自动</span></td>
              <td>激光订单收入<span class="fin-muted">（{{ income.orderCount || 0 }} 单，按订单创建月归集）</span></td>
              <td class="fin-t-right fin-num">{{ fmtMoney(income.order) }}</td>
              <td class="fin-t-right fin-muted">—</td>
            </tr>
            <!-- 废料收入（口径③）：卖废料**全额** = 重量 × 当月实际售价；实赚差价 = 全额 − Σ$fei。
                 ⚠️ 订单收入是「客户实付」（已扣掉折现给客户的那笔），所以它必须单独列一行加回来。 -->
            <tr>
              <td><span class="fin-tag fin-tag-auto">自动</span></td>
              <td>
                废料收入
                <span class="fin-muted">（卖废料全额 ＝ 废料重量 × 当月实际售价；{{ wastePriceSummaryText }}）</span>
                <span class="fin-muted fin-small" style="display:block">
                  其中实赚差价 <b>{{ fmtMoney(income.wasteMargin) }}</b>
                  ＝ 全额 {{ fmtMoney(income.waste) }} − 折现给客户 {{ fmtMoney(income.wasteFei) }}
                  <template v-if="income.wasteWeight">（废料 {{ income.wasteWeight }} kg）</template>
                  <a class="fin-link" style="margin-left:6px" @click="openWastePriceDialog">改当月售价</a>
                </span>
              </td>
              <td class="fin-t-right fin-num">{{ fmtMoney(income.waste) }}</td>
              <td class="fin-t-right fin-muted">—</td>
            </tr>
            <tr v-for="item in income.manualItems" :key="'inc' + item.id">
              <td><span class="fin-tag fin-tag-manual">手工</span></td>
              <td>{{ item.itemName }}<span class="fin-muted" v-if="item.remark">（{{ item.remark }}）</span></td>
              <td class="fin-t-right fin-num">{{ fmtMoney(item.amount) }}</td>
              <td class="fin-t-right">
                <a class="fin-link" @click="openIncomeDialog(item)">改</a>
                <a class="fin-link fin-link-danger" @click="removeIncome(item)">删</a>
              </td>
            </tr>
            <tr v-if="!(income.manualItems || []).length">
              <td colspan="4" class="fin-empty">无其他收入。<a class="fin-link" @click="openIncomeDialog">新增一笔</a></td>
            </tr>
            </tbody>
          </table>
        </section>

        <!-- 成本明细 -->
        <section class="fin-section">
          <div class="fin-section-head">
            <h3>成本明细</h3>
            <div class="fin-head-right">
              <span class="fin-section-total">合计 {{ fmtMoney(cost.total) }}</span>
              <!-- ⚠️ 这里必须用 outsourceManual（手工勾选的那部分），不能用 cost.outsource ——
                   后者 2026-10-04 起还包含「订单外协」，那部分**不在**成本明细合计里，
                   放在「合计」旁边会被误读成已含在合计内。 -->
              <span class="fin-muted fin-small"
                    v-if="cost.outsourceManual"
                    :title="'手工录入里勾了「外协」的费用合计（含焊接 / 折弯 / 运费等）。'
                             + '已含在左边的合计里，不重复计入。'
                             + '（订单报价里勾的「外协」不在成本明细内，见「最终净利计算过程」。）'">
                其中手工外协 {{ fmtMoney(cost.outsourceManual) }}
              </span>
              <button class="fin-btn fin-btn-mini fin-btn-ghost" @click="copyLastMonthExpense">复制上月</button>
              <button class="fin-btn fin-btn-mini fin-btn-ghost" @click="openSaveTemplateDialog">存为模板</button>
              <button class="fin-btn fin-btn-mini fin-btn-ghost" @click="openApplyDialog">从模板导入</button>
            </div>
          </div>
          <div v-for="group in groups" :key="group.groupKey" class="fin-group">
            <div class="fin-group-head">
              <span class="fin-group-name">{{ group.groupName }}</span>
              <span class="fin-group-badge" :class="group.isDirect === 1 ? 'fin-badge-direct' : 'fin-badge-period'">
                {{ group.isDirect === 1 ? '直接成本 · 影响毛利' : '期间费用 · 只影响净利' }}
              </span>
              <span class="fin-group-total">{{ fmtMoney(group.subtotal) }}</span>
            </div>
            <table class="fin-table fin-table-sm">
              <thead>
              <tr>
                <th style="width: 110px">来源</th>
                <th>项目</th>
                <th style="width: 160px">说明</th>
                <th style="width: 140px" class="fin-t-right">金额</th>
                <th style="width: 90px" class="fin-t-right">操作</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in group.items" :key="group.groupKey + '-' + index">
                <td>
                  <span class="fin-tag" :class="item.source === 'meter' ? 'fin-tag-auto' : 'fin-tag-manual'">
                    {{ item.sourceName }}
                  </span>
                  <!-- 外协标记：这是「费用性质」，与上面「来源(自动/手工)」不是一个维度，所以另起一个标签 -->
                  <span v-if="isOutsourceItem(item)"
                        class="fin-tag fin-tag-outsource"
                        title="这笔费用勾了「外协」：属于外协加工（焊接 / 折弯 / 运费等），经营看板单独统计为「外协成本」；金额仍按原类别计入毛利 / 净利，不重复计入。">外协</span>
                </td>
                <td>{{ item.itemName }}</td>
                <td class="fin-muted">{{ itemTip(item) }}</td>
                <td class="fin-t-right fin-num">{{ fmtMoney(item.amount) }}</td>
                <td class="fin-t-right">
                  <template v-if="item.source === 'manual'">
                    <a class="fin-link" @click="openExpenseDialog(item)">改</a>
                    <a class="fin-link fin-link-danger" @click="removeExpense(item)">删</a>
                  </template>
                  <a v-else class="fin-link" @click="openReadingDialog(item)">抄表</a>
                </td>
              </tr>
              </tbody>
            </table>
            <div class="fin-group-foot">
              <button class="fin-btn fin-btn-mini" @click="openExpenseDialog(null, group)">+ 添加{{ group.groupName }}费用</button>
            </div>
          </div>
          <p v-if="!groups.length" class="fin-empty">本月还没有任何成本记录。</p>
        </section>

        <!-- 按日明细：订单收入 + 废料收入 / 材料成本（整板采购，见下方口径说明） -->
        <section class="fin-section">
          <div class="fin-section-head">
            <h3>按日明细（{{ month }}）</h3>
            <div class="fin-head-right">
              <span class="fin-muted fin-small">材料 / 订单外协来自订单报价，已在「最终净利」中扣除；手工外协见「成本明细」</span>
              <button class="fin-btn fin-btn-mini fin-btn-ghost" @click="loadDaily">刷新</button>
            </div>
          </div>
          <table class="fin-table fin-table-sm" v-if="dailyDays.length">
            <thead>
            <tr>
              <th style="width: 46px"></th>
              <th style="width: 96px">日期</th>
              <th class="fin-t-right" style="width: 56px">单数</th>
              <th class="fin-t-right" style="width: 66px">已计价</th>
              <th class="fin-t-right">订单收入</th>
              <th class="fin-t-right">废料收入</th>
              <th class="fin-t-right">材料成本(整板)</th>
              <th class="fin-t-right">订单外协(参考)</th>
              <th class="fin-t-right">其中未定价</th>
              <th class="fin-t-right">参考毛利(全口径)</th>
              <th class="fin-t-right">参考毛利(仅已计价)</th>
              <th class="fin-t-right" style="width: 84px">参考毛利率</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="row in dailyDays">
              <tr :key="row.day">
                <td>
                  <button class="fin-btn fin-btn-mini fin-btn-ghost fin-detail-toggle"
                          :class="{ 'fin-detail-toggle-on': expandedDay === row.day }"
                          @click="toggleDayDetail(row)"
                          :title="'展开 ' + row.day + ' 的逐单、逐行计算过程'">
                    {{ expandedDay === row.day ? '收起' : '明细' }}
                  </button>
                </td>
                <td>{{ row.day }}</td>
                <td class="fin-t-right">{{ row.orderCount }}</td>
                <td class="fin-t-right fin-muted">{{ row.pricedCount }}</td>
                <td class="fin-t-right fin-num">{{ fmtMoney(row.income) }}</td>
                <td class="fin-t-right fin-num" :title="'剩板变现抵加工费；收入合计 = ' + fmtMoney(row.incomeTotal)">
                  {{ fmtMoney(row.wasteIncome) }}
                </td>
                <td class="fin-t-right fin-num">{{ fmtMoney(row.materialCost) }}</td>
                <td class="fin-t-right fin-num">{{ fmtMoney(row.outsourceCost) }}</td>
                <td class="fin-t-right fin-muted">{{ fmtMoney(row.unpricedMaterial) }}</td>
                <td class="fin-t-right fin-num" :class="{ 'fin-neg': row.refGross < 0 }">{{ fmtMoney(row.refGross) }}</td>
                <td class="fin-t-right fin-num" :class="{ 'fin-neg': row.pricedRefGross < 0 }">{{ fmtMoney(row.pricedRefGross) }}</td>
                <td class="fin-t-right">{{ row.refMargin == null ? '—' : fmtRate(row.refMargin) }}</td>
              </tr>
              <!-- 展开：这一天的逐单一行、逐行计算过程 -->
              <tr v-if="expandedDay === row.day" :key="row.day + '-detail'" class="fin-detail-row">
                <td colspan="12">
                  <p v-if="dailyDetailLoading[row.day]" class="fin-muted">正在加载 {{ row.day }} 的明细…</p>
                  <template v-else-if="dailyDetailMap[row.day]">
                    <table class="fin-table fin-table-sm fin-detail-sum">
                      <thead>
                      <tr>
                        <th>分组</th>
                        <th class="fin-t-right">单数</th>
                        <th class="fin-t-right">已计价</th>
                        <th class="fin-t-right">订单收入</th>
                        <th class="fin-t-right">废料收入</th>
                        <th class="fin-t-right">材料成本(整板)</th>
                        <th class="fin-t-right">订单外协</th>
                        <th class="fin-t-right">参考毛利(全口径)</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="group in dayDetailGroups(row.day)" :key="group.key"
                          :class="{ 'fin-detail-excluded': !group.inStats }">
                        <td>{{ group.label }}</td>
                        <td class="fin-t-right">{{ group.summary.orderCount }}</td>
                        <td class="fin-t-right fin-muted">{{ group.summary.pricedCount }}</td>
                        <td class="fin-t-right fin-num">{{ fmtMoney(group.summary.income) }}</td>
                        <td class="fin-t-right fin-num">{{ fmtMoney(group.summary.wasteIncome) }}</td>
                        <td class="fin-t-right fin-num">{{ fmtMoney(group.summary.materialCost) }}</td>
                        <td class="fin-t-right fin-num">{{ fmtMoney(group.summary.outsourceCost) }}</td>
                        <td class="fin-t-right fin-num" :class="{ 'fin-neg': group.summary.refGross < 0 }">
                          {{ fmtMoney(group.summary.refGross) }}
                        </td>
                      </tr>
                      <tr class="fin-detail-stat">
                        <td><b>参与统计合计</b>（= 上表当日那一行）</td>
                        <td class="fin-t-right"><b>{{ dailyDetailMap[row.day].stat.orderCount }}</b></td>
                        <td class="fin-t-right fin-muted">{{ dailyDetailMap[row.day].stat.pricedCount }}</td>
                        <td class="fin-t-right fin-num"><b>{{ fmtMoney(dailyDetailMap[row.day].stat.income) }}</b></td>
                        <td class="fin-t-right fin-num"><b>{{ fmtMoney(dailyDetailMap[row.day].stat.wasteIncome) }}</b></td>
                        <td class="fin-t-right fin-num"><b>{{ fmtMoney(dailyDetailMap[row.day].stat.materialCost) }}</b></td>
                        <td class="fin-t-right fin-num"><b>{{ fmtMoney(dailyDetailMap[row.day].stat.outsourceCost) }}</b></td>
                        <td class="fin-t-right fin-num"><b>{{ fmtMoney(dailyDetailMap[row.day].stat.refGross) }}</b></td>
                      </tr>
                      </tbody>
                    </table>

                    <div v-for="group in dayDetailGroups(row.day)" :key="group.key + '-orders'" class="fin-detail-group">
                      <h4 class="fin-detail-group-title">
                        {{ group.label }}（{{ group.summary.orderCount }} 单）
                        <span v-if="!group.inStats" class="fin-muted fin-small">— 按规则 1 排除，不计入任何合计</span>
                      </h4>
                      <p v-if="!group.orders.length" class="fin-muted fin-small">（无）</p>
                      <div v-for="order in group.orders" :key="order.id" class="fin-detail-order">
                        <div class="fin-detail-order-head">
                          <span class="fin-detail-oid">#{{ order.id }}</span>
                          <span class="fin-muted">{{ order.time }}</span>
                          <span class="fin-detail-cust">{{ order.customer || '（无客户名）' }}</span>
                          <span class="fin-muted fin-detail-title" :title="order.title">{{ order.title || '（无标题）' }}</span>
                          <span class="fin-detail-badge" :class="order.priced ? 'fin-detail-priced' : 'fin-detail-unpriced'">
                            {{ order.priced ? '已计价' : '未定价' }}
                          </span>
                        </div>
                        <div class="fin-detail-figs">
                          <span>订单金额 <b>{{ fmtMoney(order.money) }}</b><span
                            class="fin-muted" title="订单金额是「客户实付」，已经抵掉了这笔剩板回收">（客户实付）</span></span>
                          <span :title="orderMaterialTip(order)">材料成本 <b>{{ fmtMoney(order.materialCost) }}</b>
                            <span class="fin-muted">（整板采购）</span>
                          </span>
                          <span v-if="orderWasteRecovery(order) > 0"
                                title="剩板客户不拉走，由我方变现并抵掉他的加工费；订单金额（客户实付）里已经少收了这笔，所以它计入收入侧。看板不体现卖剩板时的场地占用/整理/装车等杂费。">
                            废料收入 <b>{{ fmtMoney(orderWasteRecovery(order)) }}</b>
                          </span>
                          <span v-if="order.outsourceCost">订单外协 <b>{{ fmtMoney(order.outsourceCost) }}</b></span>
                          <span>参考毛利 <b :class="{ 'fin-neg': order.refGross < 0 }">{{ fmtMoney(order.refGross) }}</b></span>
                          <span class="fin-muted">明细 {{ order.lineCount }} 行（跳过 {{ order.skippedCount }} 行）</span>
                        </div>
                        <table v-if="order.lines && order.lines.length" class="fin-table fin-table-sm fin-detail-lines">
                          <thead>
                          <tr>
                            <th style="width: 50px">行</th>
                            <th style="width: 40%">报价参数</th>
                            <th>计算过程</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="line in order.lines" :key="line.index"
                              :class="{ 'fin-detail-skipped': !!line.reason }">
                            <td class="fin-muted">行{{ line.index }}</td>
                            <td class="fin-detail-params">
                              <span>厚 t <code>{{ fmtSizeRaw(line.rawT, line.thickness) }}</code></span>
                              <span>长 h <code>{{ fmtSizeRaw(line.rawH, line.lengthMm) }}</code></span>
                              <span>宽 w <code>{{ fmtSizeRaw(line.rawW, line.widthMm) }}</code></span>
                              <span>密度 m <code>{{ line.rawM == null || line.rawM === '' ? '—' : line.rawM }}</code></span>
                              <span>包料 <code :class="{ 'fin-detail-baoliao-false': line.baoliao === false }">{{
                                  line.baoliao === undefined || line.baoliao === null ? '未设置' : String(line.baoliao)
                                }}</code></span>
                              <span v-if="line.matKey" class="fin-muted">{{ line.matKey }}</span>
                              <span v-if="line.outsource" style="color:#e6a23c;"
                                    title="本行「其他」金额在激光订单里勾了「外协」（运费 / 焊接 / 折弯等代垫费用），计入「订单外协(参考)」">
                                外协 ¥{{ fmtMoney(line.outsource) }}
                              </span>
                            </td>
                            <td class="fin-detail-formula">{{ lineFormula(line) }}</td>
                          </tr>
                          </tbody>
                          <tfoot>
                          <tr>
                            <td colspan="2" class="fin-t-right">= 材料成本（整板采购，计入净利）</td>
                            <td class="fin-detail-formula"><b>{{ fmtMoney(order.materialCost) }}</b></td>
                          </tr>
                          <tr v-if="orderWasteRecovery(order) > 0">
                            <td colspan="2" class="fin-t-right">
                              ＋ 废料收入（剩板客户不拉走，由我方变现并抵掉他的加工费；<b>收入侧</b>加项，不减材料成本）
                            </td>
                            <td class="fin-detail-formula"><b>{{ fmtMoney(orderWasteRecovery(order)) }}</b></td>
                          </tr>
                          <tr v-if="order.outsourceCost">
                            <td colspan="2" class="fin-t-right">其中订单外协（订单「其他」列勾了「外协」的金额，与材料成本相互独立）</td>
                            <td class="fin-detail-formula"><b>{{ fmtMoney(order.outsourceCost) }}</b></td>
                          </tr>
                          </tfoot>
                        </table>
                        <p v-else class="fin-muted fin-small">
                          该单<b>无报价明细</b>（price_info 为空）⇒ 材料成本恒为 ¥0.00
                        </p>
                      </div>
                    </div>
                  </template>
                  <p v-else class="fin-muted">明细加载失败，请点「刷新」重试。</p>
                </td>
              </tr>
            </template>
            </tbody>
            <tfoot>
            <tr>
              <td></td>
              <td>合计</td>
              <td class="fin-t-right">{{ dailyTotal.orderCount || 0 }}</td>
              <td class="fin-t-right fin-muted">{{ dailyTotal.pricedCount || 0 }}</td>
              <td class="fin-t-right fin-num">{{ fmtMoney(dailyTotal.income) }}</td>
              <td class="fin-t-right fin-num" :title="'收入合计 = ' + fmtMoney(dailyTotal.incomeTotal)">
                {{ fmtMoney(dailyTotal.wasteIncome) }}
              </td>
              <td class="fin-t-right fin-num">{{ fmtMoney(dailyTotal.materialCost) }}</td>
              <td class="fin-t-right fin-num">{{ fmtMoney(dailyTotal.outsourceCost) }}</td>
              <td class="fin-t-right fin-muted">{{ fmtMoney(dailyTotal.unpricedMaterial) }}</td>
              <td class="fin-t-right fin-num" :class="{ 'fin-neg': dailyTotal.refGross < 0 }">{{ fmtMoney(dailyTotal.refGross) }}</td>
              <td class="fin-t-right fin-num" :class="{ 'fin-neg': dailyTotal.pricedRefGross < 0 }">{{ fmtMoney(dailyTotal.pricedRefGross) }}</td>
              <td class="fin-t-right">{{ dailyTotal.refMargin == null ? '—' : fmtRate(dailyTotal.refMargin) }}</td>
            </tr>
            </tfoot>
          </table>
          <p v-else class="fin-empty">本月还没有订单，所以按日没有数据。</p>
          <p class="fin-hint">
            <b>规则 1（第一优先，无例外）</b>：标题里<b>出现「先报价」三个字</b>的订单就是报价单 ——
            <b>不管是否有价格、不管材料多少</b>，一律不参与统计：不计收入、不计单数、
            其报价明细也不复算材料成本（即「报价不参与统计」）。这条规则不被任何其他口径覆盖，
            所以「已经填了价格」但只要标题还带这三个字的单，同样会被剔除。
            上表剩下的订单中，仍可能有金额为 0 的「<b>未定价</b>」单（标题里没写「先报价」的那些）：
            材料成本已发生、收入还没落定，混在一起会把当日参考毛利压成负数，
            所以同时给两个口径——「参考毛利(全口径)」含这些未定价单的材料成本、
            「参考毛利(仅已计价)」只算已定价单，「其中未定价」列即这些单的材料成本。
            材料成本按订单报价明细复算，只算我方供料的行（厚板按 kg、薄板按 m²）。
            口径为<b>整板采购</b>：<b>材料成本 ＝ 整板采购额，不减剩板回收</b>。
            剩板是「<b>客户不拉走、由我方卖掉变现</b>」的：变现的钱进了我们账户，
            但已经在订单金额里抵掉了客户的加工费（<b>订单收入是「客户实付」，少收了那一笔</b>），
            所以它<b>不在成本侧冲减，而是作为「废料收入」加到收入侧</b> ——
            两项写在两处、数值最终等价，不会重复也不会漏。
            ⚠️ 看板<b>不体现</b>卖剩板时产生的场地占用 / 整理 / 装车等杂费，只按订单里录入的剩板金额计算，故本项略偏乐观。
            ⚠️ 口径提示：本表是<b>日粒度参考口径</b>（「参考毛利」＝当日收入 <b>＋</b> 当日废料收入 − 当日材料成本(整板) − 当日订单外协，<b>不含</b>手工录入的房租/工资）；
            而顶部指标卡的<b>最终净利</b>是月粒度，会额外扣除直接成本与期间费用。两者别混着对。
            <b>订单外协</b>（订单「其他」列勾了「外协」的金额）已在最终净利中扣除（见「最终净利计算过程」）。
            注意口径区分：<b>手工录入费用里勾了「外协」的钱属于成本侧</b>，已按费用类别计入「成本明细」，
            且是<b>月粒度</b>（费用只有归属月份、没有具体日期），所以<b>不进这张按日表</b>，
            只在顶部指标卡与「最终净利计算过程」里体现。
            若本月明明有订单却显示为空，说明后端 <code>/fin/daily</code> 接口尚未部署。
            <br>
            点每行最左的「<b>明细</b>」按钮，会展开这一天的<b>逐单一行</b>：单号、时间、客户、标题、金额、材料成本，
            以及每一张单<b>报价明细的逐行计算过程</b>（厚/长/宽原值 → 面积 → 按 kg 还是按 m² → 单价×数量 → 行金额，
            被跳过的行给出原因）。展开时按标题分成三组：<b>仅报价</b>（标题含「先报价」，按规则 1 排除、不计入任何合计）、
            <b>参与统计·标题以「其他」开头</b>、<b>参与统计·标题不以「其他」开头</b>；
            后两组之和就是上表当日那一行。
            ⚠️ 尺寸字段里若写了算式（如 <code>525/2</code>、<code>216*2</code>、<code>180*177</code>），
            一律按<b>等价求值</b>参与计算，与你录入报价时的口径一致（<code>180*177</code> 表示 177 件 180mm）。
          </p>
        </section>
      </div>

      <!-- 右栏：抄表 + 趋势 + 参考 -->
      <div class="fin-col fin-col-side">
        <!-- 抄表 -->
        <section class="fin-section">
          <div class="fin-section-head">
            <h3>抄表（{{ month }}）</h3>
            <div class="fin-head-right">
              <button class="fin-btn fin-btn-mini fin-btn-ghost" @click="openMeterDialog">表具管理</button>
              <button class="fin-btn fin-btn-mini" @click="openReadingDialog(null)">录入读数</button>
            </div>
          </div>
          <table class="fin-table fin-table-sm">
            <thead>
            <tr>
              <th>表具</th>
              <th class="fin-t-right">上期</th>
              <th class="fin-t-right">本期</th>
              <th class="fin-t-right">用量</th>
              <th class="fin-t-right">单价</th>
              <th class="fin-t-right">金额</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in meterRows" :key="'m' + item.meterId" :class="{ 'fin-row-pending': item.pending }">
              <td>{{ item.meterName }}</td>
              <td class="fin-t-right fin-muted">{{ fmtReading(item.previousReading) }}</td>
              <td class="fin-t-right">{{ fmtReading(item.currentReading) }}</td>
              <td class="fin-t-right">{{ item.usage == null ? '—' : fmtNumber(item.usage) + item.unitName }}</td>
              <td class="fin-t-right fin-muted">{{ fmtNumber(item.unitPrice) }}</td>
              <td class="fin-t-right fin-num">{{ item.amount == null ? '未抄表' : fmtMoney(item.amount) }}</td>
            </tr>
            <tr v-if="!meterRows.length">
              <td colspan="6" class="fin-empty">
                还没有配置表具，所以这里没有可选的表。
                <a class="fin-link" @click="openMeterDialog">去「表具管理」新增一个</a>
              </td>
            </tr>
            </tbody>
          </table>
        </section>

        <!-- 趋势 -->
        <section class="fin-section">
          <div class="fin-section-head">
            <h3>近 12 月趋势</h3>
            <span class="fin-muted fin-small">截至 {{ month }}</span>
          </div>
          <div ref="trendChart" class="fin-chart"></div>
        </section>

        <!-- 最终净利计算过程（2026-10-04 新增：把每一笔加减都摊开，便于人工核对） -->
        <section class="fin-section">
          <div class="fin-section-head">
            <h3>最终净利计算过程</h3>
            <span class="fin-section-total">净利 {{ fmtMoney(profit.net) }}</span>
          </div>
          <table class="fin-table fin-table-sm">
            <tbody>
            <tr>
              <td>订单收入<span class="fin-muted">（{{ income.orderCount || 0 }} 单，按订单创建月归集；<b>这是「客户实付」</b>，已抵掉剩板回收）</span></td>
              <td class="fin-t-right fin-num">{{ fmtMoney(income.order) }}</td>
            </tr>
            <tr>
              <td>+ 其他收入<span class="fin-muted">（手工录入 {{ (income.manualItems || []).length }} 笔）</span></td>
              <td class="fin-t-right fin-num">+{{ fmtMoney(income.manual) }}</td>
            </tr>
            <!-- 废料收入：剩板客户**不拉走**，由我方卖掉变现、变现款**抵他的加工费**。
                 订单收入是「客户实付」已经少收了这笔 ⇒ 必须在收入侧单独加回来，
                 成本侧材料成本则照整板采购全额计（不减剩板回收）。 -->
            <tr>
              <td>+ 废料收入<span class="fin-muted">（卖废料全额＝重量 × 当月实际售价；客户不拉走剩板，我方代卖变现）</span>
                <span class="fin-muted fin-small" style="display:block">
                  其中实赚差价 <b>{{ fmtMoney(income.wasteMargin) }}</b> ＝ 全额 {{ fmtMoney(income.waste) }}
                  − 折现给客户 {{ fmtMoney(income.wasteFei) }}
                </span>
              </td>
              <td class="fin-t-right fin-num">+{{ fmtMoney(income.waste) }}</td>
            </tr>
            <tr class="fin-row-subtotal">
              <td><b>= 总收入</b></td>
              <td class="fin-t-right fin-num"><b>{{ fmtMoney(income.total) }}</b></td>
            </tr>
            <tr>
              <td>− 直接成本<span class="fin-muted">（抄表 {{ fmtMoney(cost.directMeter) }} · 手工 {{ fmtMoney(cost.directManual) }}）</span></td>
              <td class="fin-t-right fin-num">−{{ fmtMoney(cost.direct) }}</td>
            </tr>
            <tr>
              <td>− 期间费用<span class="fin-muted">（房租 / 工资 / 管理费等）</span></td>
              <td class="fin-t-right fin-num">−{{ fmtMoney(cost.period) }}</td>
            </tr>
            <tr class="fin-row-subtotal">
              <td><b>= 现金净利（只扣手工录入与抄表费用）</b></td>
              <td class="fin-t-right fin-num"><b>{{ fmtMoney(profit.cashNet) }}</b></td>
            </tr>
            <tr>
              <td>− 材料成本<span class="fin-muted">（<b>整板采购</b>；剩板回收<b>不</b>在这里冲减，已作「+ 废料收入」加在收入侧）</span></td>
              <td class="fin-t-right fin-num">−{{ ref.materialCost == null ? '—' : fmtMoney(ref.materialCost) }}</td>
            </tr>
            <tr>
              <td>− 订单外协（激光订单「其他」列勾了「外协」的金额，含运费 / 焊接 / 折弯等）</td>
              <td class="fin-t-right fin-num">−{{ ref.outsourceCost == null ? '—' : fmtMoney(ref.outsourceCost) }}</td>
            </tr>
            <tr class="fin-row-subtotal">
              <td><b>= 最终净利</b></td>
              <td class="fin-t-right fin-num"><b :class="{ 'fin-neg': profit.net < 0 }">{{ fmtMoney(profit.net) }}</b></td>
            </tr>
            <tr>
              <td colspan="2" class="fin-muted fin-small" style="border-top:1px dashed #e3e8ee;padding-top:8px;">
                全口径成本合计＝现金成本 {{ fmtMoney(cost.total) }} ＋ 材料成本 {{ fmtMoney(cost.refMaterial) }}
                ＋ 订单外协 {{ fmtMoney(cost.outsourceOrder) }} ＝ <b>{{ fmtMoney(cost.full) }}</b>；
                最终净利 ＝ 总收入<b>（含废料收入 {{ fmtMoney(income.waste) }}）</b> − 全口径成本合计。
                下方为两个成本细分口径（<b>不</b>再重复相加）。
              </td>
            </tr>
            <tr>
              <td>外协成本合计（订单外协 ＋ 手工勾选的外协）<span class="fin-muted">（已含在最终净利的扣减中，不重复扣）</span></td>
              <td class="fin-t-right fin-num">{{ fmtMoney(cost.outsource) }}</td>
            </tr>
            <tr>
              <td class="fin-muted fin-small">其中：订单外协 {{ fmtMoney(cost.outsourceOrder) }}（报价明细勾选） ＋ 手工外协 {{ fmtMoney(cost.outsourceManual) }}（{{ cost.outsourceCount || 0 }} 笔，已含在直接/期间费用内）</td>
              <td class="fin-t-right fin-muted fin-small">
                {{ fmtMoney(cost.outsourceDirect) }} / {{ fmtMoney(cost.outsourcePeriod) }}
              </td>
            </tr>
            <tr>
              <td>订单子项合计（代买料 / 外协等，仅参考、不重复计入）</td>
              <td class="fin-t-right fin-num">{{ fmtMoney(ref.orderItemFee) }}</td>
            </tr>
            </tbody>
          </table>
          <p class="fin-note">{{ ref.note }}</p>
        </section>
      </div>
    </div>

    <!-- ===================== 弹窗：费用 ===================== -->
    <div class="fin-mask" v-if="expenseDialog.show" @click.self="expenseDialog.show = false">
      <div class="fin-dialog">
        <div class="fin-dialog-head">
          <h3>{{ expenseDialog.form.id ? '修改费用' : '记一笔费用' }}</h3>
          <a class="fin-dialog-close" @click="expenseDialog.show = false">✕</a>
        </div>
        <div class="fin-dialog-body">
          <div class="fin-field">
            <label>归属月份</label>
            <input class="fin-input" v-model="expenseDialog.form.month" placeholder="yyyy-MM">
          </div>
          <div class="fin-field">
            <label>费用类别</label>
            <select class="fin-input" v-model="expenseDialog.form.categoryId">
              <option :value="null">请选择</option>
              <optgroup v-for="group in categoryGroups" :key="group.groupKey" :label="group.groupName">
                <option v-for="category in group.categories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="fin-field">
            <label>明细名</label>
            <input class="fin-input" v-model="expenseDialog.form.itemName" placeholder="如：5号铺房租、9月工资-张三">
          </div>
          <div class="fin-field">
            <label>金额</label>
            <input class="fin-input" type="number" v-model="expenseDialog.form.amount" placeholder="0.00">
          </div>
          <!-- 外协标记：与费用类别无关（外协形式多样，不细化种类，只区分「是不是外协」）。
               勾选后经营看板把它统计进「外协成本」（与订单报价里勾的「外协」相加）；
               该笔费用仍按上面的类别计入直接成本 / 期间费用，进而影响毛利与净利，不重复计入。 -->
          <div class="fin-field">
            <label>外协</label>
            <label class="fin-check"
                   title="勾选后这笔金额会在经营看板里单独统计为「外协成本」（焊接 / 折弯 / 运费等外协加工费，不细分种类）。">
              <input type="checkbox"
                     v-model="expenseDialog.form.isOutsource"
                     :true-value="1"
                     :false-value="0">
              <span>这笔是外协加工（焊接 / 折弯 / 运费等）</span>
            </label>
            <p class="fin-hint">
              勾选只加一个统计维度：金额仍按上面的费用类别计入毛利 / 净利，不会重复计入。
            </p>
          </div>
          <div class="fin-field">
            <label>备注</label>
            <input class="fin-input" v-model="expenseDialog.form.remark" placeholder="可不填">
          </div>
        </div>
        <div class="fin-dialog-foot">
          <button class="fin-btn fin-btn-ghost" @click="expenseDialog.show = false">取消</button>
          <button class="fin-btn" @click="saveExpense">保存</button>
        </div>
      </div>
    </div>

    <!-- ===================== 弹窗：其他收入 ===================== -->
    <div class="fin-mask" v-if="incomeDialog.show" @click.self="incomeDialog.show = false">
      <div class="fin-dialog">
        <div class="fin-dialog-head">
          <h3>{{ incomeDialog.form.id ? '修改收入' : '新增其他收入' }}</h3>
          <a class="fin-dialog-close" @click="incomeDialog.show = false">✕</a>
        </div>
        <div class="fin-dialog-body">
          <div class="fin-field">
            <label>归属月份</label>
            <input class="fin-input" v-model="incomeDialog.form.month" placeholder="yyyy-MM">
          </div>
          <div class="fin-field">
            <label>项目</label>
            <input class="fin-input" v-model="incomeDialog.form.itemName" placeholder="如：废料变卖">
          </div>
          <div class="fin-field">
            <label>金额</label>
            <input class="fin-input" type="number" v-model="incomeDialog.form.amount" placeholder="0.00">
          </div>
          <div class="fin-field">
            <label>备注</label>
            <input class="fin-input" v-model="incomeDialog.form.remark" placeholder="可不填">
          </div>
        </div>
        <div class="fin-dialog-foot">
          <button class="fin-btn fin-btn-ghost" @click="incomeDialog.show = false">取消</button>
          <button class="fin-btn" @click="saveIncome">保存</button>
        </div>
      </div>
    </div>

    <!-- ===================== 弹窗：当月废料实际售价（口径③） ===================== -->
    <div class="fin-mask" v-if="wastePriceDialog.show" @click.self="wastePriceDialog.show = false">
      <div class="fin-dialog">
        <div class="fin-dialog-head">
          <h3>当月废料实际售价（{{ wastePriceDialog.month }}）</h3>
          <a class="fin-dialog-close" @click="wastePriceDialog.show = false">✕</a>
        </div>
        <div class="fin-dialog-body">
          <p class="fin-hint">
            填「这个月废料实际能卖多少钱一公斤」。看板据此算
            <b>废料收入 ＝ 废料重量 × 该材质的当月售价</b>，
            <b>实赚差价 ＝ 废料收入 − 折现给客户的钱</b>。<br>
            留空 ＝ 未配价：该材质退回按订单里录的折现单价（$s）计价 —— 与旧口径一致、差价记 0。
          </p>
          <div class="fin-field" v-for="bucket in wasteBuckets" :key="bucket.key">
            <label>{{ bucket.name }}</label>
            <input class="fin-input" type="number" step="0.01" min="0"
                   v-model="wastePriceDialog.prices[bucket.key]"
                   :placeholder="bucket.hint">
          </div>
        </div>
        <div class="fin-dialog-foot">
          <button class="fin-btn fin-btn-ghost" @click="wastePriceDialog.show = false">取消</button>
          <button class="fin-btn" @click="saveWastePrice" :disabled="wastePriceDialog.saving">
            {{ wastePriceDialog.saving ? '保存中…' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===================== 弹窗：抄表录入 ===================== -->
    <div class="fin-mask" v-if="readingDialog.show" @click.self="readingDialog.show = false">
      <div class="fin-dialog">
        <div class="fin-dialog-head">
          <h3>抄表录入</h3>
          <a class="fin-dialog-close" @click="readingDialog.show = false">✕</a>
        </div>
        <div class="fin-dialog-body">
          <div class="fin-field">
            <label>表具</label>
            <select class="fin-input" v-model="readingDialog.form.meterId" @change="loadLastReading">
              <option :value="null">请选择</option>
              <option v-for="meter in activeMeters" :key="meter.id" :value="meter.id">
                {{ meter.name }}（{{ meter.unitPrice }}{{ meter.unitName }}）
              </option>
            </select>
            <p class="fin-hint fin-hint-warn" v-if="!activeMeters.length">
              还没有可用的表具。
              <a class="fin-link" @click="gotoAddMeter">点这里新增一个</a>
              （先填名称、单价，例如普通电表 1.5、特殊电表 60）。
            </p>
            <p class="fin-hint" v-else>
              下拉里没有你要的表，或要改名称/单价？
              <a class="fin-link" @click="gotoMeterDialog">去「表具管理」</a>
            </p>
          </div>
          <div class="fin-field">
            <label>抄表日期</label>
            <input class="fin-input" type="date" v-model="readingDialog.form.readDate">
          </div>
          <div class="fin-field">
            <label>抄见读数</label>
            <input class="fin-input" type="number" v-model="readingDialog.form.reading" placeholder="0">
            <p class="fin-hint" v-if="readingDialog.lastReading">
              该表最近一次读数：{{ fmtReading(readingDialog.lastReading.reading) }}
              （{{ readingDialog.lastReading.readDate }}）
            </p>
          </div>
          <div class="fin-field">
            <label>备注</label>
            <input class="fin-input" v-model="readingDialog.form.remark" placeholder="可不填">
          </div>
          <p class="fin-hint">同一张表同一天重复提交会覆盖旧读数；用量 = 本次读数 − 上一条读数。</p>
        </div>
        <div class="fin-dialog-foot">
          <button class="fin-btn fin-btn-ghost" @click="readingDialog.show = false">取消</button>
          <button class="fin-btn" @click="saveReading">保存</button>
        </div>
      </div>
    </div>

    <!-- ===================== 弹窗：表具管理 ===================== -->
    <div class="fin-mask" v-if="meterDialog.show" @click.self="meterDialog.show = false">
      <div class="fin-dialog fin-dialog-wide">
        <div class="fin-dialog-head">
          <h3>表具管理（电表 / 水表）</h3>
          <a class="fin-dialog-close" @click="meterDialog.show = false">✕</a>
        </div>
        <div class="fin-dialog-body">
          <table class="fin-table fin-table-sm">
            <thead>
            <tr>
              <th style="width: 150px">名称</th>
              <th style="width: 100px">类型</th>
              <th style="width: 170px">归属类别</th>
              <th style="width: 110px">单价</th>
              <th style="width: 80px">单位</th>
              <th style="width: 110px">起始读数</th>
              <th style="width: 80px">启用</th>
              <th style="width: 80px" class="fin-t-right">操作</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(meter, meterIndex) in meters" :key="meter.id === null || meter.id === undefined ? 'new-' + meterIndex : 'm-' + meter.id">
              <td><input class="fin-input fin-input-mini" v-model="meter.name" placeholder="如：总电表"></td>
              <td>
                <select class="fin-input fin-input-mini" v-model="meter.meterType" @change="onMeterTypeChange(meter)">
                  <option value="electric">电表</option>
                  <option value="water">水表</option>
                  <option value="other">其他</option>
                </select>
              </td>
              <td>
                <select class="fin-input fin-input-mini" v-model="meter.categoryId">
                  <option :value="null">未归类</option>
                  <option v-for="category in meterCategories" :key="category.id" :value="category.id">
                    {{ category.name }}
                  </option>
                </select>
              </td>
              <td><input class="fin-input fin-input-mini" type="number" v-model="meter.unitPrice"></td>
              <td><input class="fin-input fin-input-mini" v-model="meter.unitName"></td>
              <td><input class="fin-input fin-input-mini" type="number" v-model="meter.baseReading"></td>
              <td>
                <select class="fin-input fin-input-mini" v-model="meter.isActive">
                  <option :value="1">启用</option>
                  <option :value="0">停用</option>
                </select>
              </td>
              <td class="fin-t-right">
                <a class="fin-link" @click="saveMeter(meter)">存</a>
                <a class="fin-link fin-link-danger" @click="removeMeter(meter)">删</a>
              </td>
            </tr>
            <tr v-if="!meters.length">
              <td colspan="8" class="fin-empty">还没有表具。点下面「新增表具」加一个。</td>
            </tr>
            </tbody>
          </table>
          <p class="fin-hint">
            单价按表设置：1.5 元/度的普通电表填 1.5；60 元/度的特殊电表填 60，用量照读数差计算。
            新增的行点右下角「全部保存」会一起存下来（也可以单独点行尾「存」）。
          </p>
        </div>
        <div class="fin-dialog-foot">
          <button class="fin-btn fin-btn-ghost" @click="addMeter">新增表具</button>
          <button class="fin-btn fin-btn-ghost" @click="gotoReadingDialog">去录入读数</button>
          <button class="fin-btn" @click="saveAllMeters">全部保存</button>
        </div>
      </div>
    </div>

    <!-- ===================== 弹窗：把本月存为费用模板 ===================== -->
    <div class="fin-mask" v-if="saveTemplateDialog.show" @click.self="saveTemplateDialog.show = false">
      <div class="fin-dialog">
        <div class="fin-dialog-head">
          <h3>把 {{ month }} 的费用存为模板</h3>
          <a class="fin-dialog-close" @click="saveTemplateDialog.show = false">✕</a>
        </div>
        <div class="fin-dialog-body">
          <div class="fin-field">
            <label>模板名称</label>
            <input class="fin-input" v-model="saveTemplateDialog.name" placeholder="如：标准月度费用">
          </div>
          <p class="fin-hint">
            只保存「手工录入」的费用（抄表项按读数自动算，不入模板）。
            共 {{ saveTemplateDialog.items.length }} 条，合计 {{ fmtMoney(saveTemplateTotal) }}。
          </p>
          <table class="fin-table fin-table-sm">
            <thead>
            <tr>
              <th>类别</th>
              <th>项目</th>
              <th class="fin-t-right" style="width: 120px">金额</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in saveTemplateDialog.items" :key="'save-tpl-' + index">
              <td>{{ item.categoryName }}</td>
              <td>{{ item.itemName }}</td>
              <td class="fin-t-right fin-num">{{ fmtMoney(item.amount) }}</td>
            </tr>
            </tbody>
          </table>
          <p class="fin-hint fin-hint-warn" v-if="sameNameTemplate">
            已存在同名模板「{{ sameNameTemplate.name }}」（{{ sameNameTemplate.sourceMonth }} 存的），
            保存会用本月的 {{ saveTemplateDialog.items.length }} 条覆盖它。
          </p>
        </div>
        <div class="fin-dialog-foot">
          <button class="fin-btn fin-btn-ghost" @click="saveTemplateDialog.show = false">取消</button>
          <button class="fin-btn" @click="confirmSaveTemplate">保存模板</button>
        </div>
      </div>
    </div>

    <!-- ===================== 弹窗：从费用模板导入 ===================== -->
    <div class="fin-mask" v-if="applyDialog.show" @click.self="applyDialog.show = false">
      <div class="fin-dialog fin-dialog-wide">
        <div class="fin-dialog-head">
          <h3>从模板导入到 {{ month }}</h3>
          <a class="fin-dialog-close" @click="applyDialog.show = false">✕</a>
        </div>
        <div class="fin-dialog-body">
          <table class="fin-table fin-table-sm" v-if="templates.length">
            <thead>
            <tr>
              <th style="width: 56px">选择</th>
              <th>模板名称</th>
              <th style="width: 110px">来源月</th>
              <th style="width: 80px" class="fin-t-right">条目</th>
              <th style="width: 150px">保存时间</th>
              <th style="width: 60px" class="fin-t-right">操作</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="template in templates" :key="template.id">
              <td>
                <input type="radio" :value="template.id" v-model="applyDialog.templateId" @change="refreshApplyPreview">
              </td>
              <td>{{ template.name }}</td>
              <td class="fin-muted">{{ template.sourceMonth }}</td>
              <td class="fin-t-right">{{ (template.items || []).length }}</td>
              <td class="fin-muted">{{ template.createdAt }}</td>
              <td class="fin-t-right">
                <a class="fin-link fin-link-danger" @click="removeTemplate(template)">删</a>
              </td>
            </tr>
            </tbody>
          </table>
          <template v-if="applyDialog.templateId">
            <p class="fin-hint">
              导入 {{ month }}：将新增 <b>{{ applyDialog.toAdd.length }}</b> 条，
              跳过 <b>{{ applyDialog.skipped.length }}</b> 条（本月已有「同类别 + 同项目名」，不会覆盖你改过的金额）。
            </p>
            <table class="fin-table fin-table-sm">
              <thead>
              <tr>
                <th style="width: 70px">结果</th>
                <th>类别</th>
                <th>项目</th>
                <th class="fin-t-right" style="width: 120px">金额</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, index) in applyDialog.rows" :key="'apply-tpl-' + index">
                <td>
                  <span class="fin-tag" :class="row.skip ? 'fin-tag-manual' : 'fin-tag-auto'">
                    {{ row.skip ? '跳过' : '新增' }}
                  </span>
                </td>
                <td>{{ row.categoryName }}</td>
                <td>{{ row.itemName }}</td>
                <td class="fin-t-right fin-num">{{ fmtMoney(row.amount) }}</td>
              </tr>
              </tbody>
            </table>
          </template>
          <p class="fin-hint">
            模板存在这台电脑的浏览器里（换电脑 / 清缓存会丢）。要每月都用的固定费用先存一次，以后每月点「从模板导入」即可。
          </p>
        </div>
        <div class="fin-dialog-foot">
          <button class="fin-btn fin-btn-ghost" @click="applyDialog.show = false">取消</button>
          <button class="fin-btn" :disabled="applyDialog.busy || !applyDialog.toAdd.length" @click="confirmApplyTemplate">
            {{ applyDialog.busy ? '导入中…' : '导入 ' + applyDialog.toAdd.length + ' 条' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'

// 费用模板存在浏览器本地：模板是「一组手工费用的快照」，不新增后端表也能用。
// 代价是换电脑/清缓存会丢（界面上已提示）；若以后要跨设备共享，再后端化。
const TEMPLATE_STORE_KEY = 'fin_expense_templates_v1'
// 模板数量上限，避免本地存储被无意义地堆满
const TEMPLATE_MAX = 20

export default {
  name: 'Finance',
  data() {
    return {
      month: '',
      summary: {},
      groups: [],
      trend: [],
      daily: { days: [], total: {} },
      /** 按日明细展开：day -> detail（后端 /fin/daily?day= 返回的 detail 段） */
      dailyDetailMap: {},
      dailyDetailLoading: {},
      /** 当前展开的日期（同一时刻只展开一天，避免一次拉太多） */
      expandedDay: '',
      categories: [],
      meters: [],
      expenseDialog: { show: false, form: {} },
      incomeDialog: { show: false, form: {} },
      /** 口径③：当月废料实际售价（材质桶 → 元/kg）。留空 = 未配价，退回按 $s 计价。 */
      wastePriceDialog: { show: false, month: '', prices: { carbon: null, '201': null, '304': null }, saving: false },
      readingDialog: { show: false, form: {}, lastReading: null },
      meterDialog: { show: false },
      templates: [],
      saveTemplateDialog: { show: false, name: '', items: [] },
      applyDialog: { show: false, templateId: null, rows: [], toAdd: [], skipped: [], busy: false },
      chart: null
    }
  },
  computed: {
    income() {
      return this.summary.income || {}
    },
    cost() {
      return this.summary.cost || {}
    },
    profit() {
      return this.summary.profit || {}
    },
    ref() {
      return this.summary.ref || {}
    },
    /**
     * 「外协成本」卡片的悬浮说明。
     * 2026-10-04 起该口径 = 订单外协（报价明细勾选）+ 手工外协（费用里勾选），
     * 必须把「两部分来源不同、相加不重复」说清楚。
     */
    outsourceCostTip() {
      let order = this.fmtMoney(this.cost.outsourceOrder)
      let manual = this.fmtMoney(this.cost.outsourceManual)
      let count = this.cost.outsourceCount || 0
      return '外协成本 = 订单外协 ' + order + '（激光订单报价明细「其他」列勾了「外协」的金额）\n'
        + '＋ 手工外协 ' + manual + '（费用明细里勾了「外协」的，共 ' + count + ' 笔）。\n'
        + '订单那部分来自报价记录、手工那部分已含在直接成本 / 期间费用内，\n'
        + '两部分相加正好等于最终净利里被扣掉的外协总额，不会重复扣。'
    },
    /** 口径③ 的三个材质桶（必须与后端 FinController.WASTE_BUCKETS / 离线 lib 一致） */
    wasteBuckets() {
      return [
        { key: 'carbon', name: '碳钢类（铁板 / 锰板 / 镀锌等）', hint: '元/kg（如 2.0；留空＝按折现单价）' },
        { key: '201', name: '201 不锈钢', hint: '元/kg（如 4.0；留空＝按折现单价）' },
        { key: '304', name: '304 不锈钢', hint: '元/kg（如 10.0；留空＝按折现单价）' }
      ]
    },
    /** 当月废料售价配置的可读摘要（收入明细行里用）：碳钢 2.00 / 201 未配 / 304 未配 */
    wastePriceSummaryText() {
      let prices = this.income.wastePrices || {}
      let text = this.wasteBuckets.map(bucket => {
        let price = prices[bucket.key]
        let shortName = bucket.key === 'carbon' ? '碳钢' : bucket.key
        return shortName + ' ' + (price > 0 ? Number(price).toFixed(2) + ' 元/kg' : '未配价(按折现单价)')
      }).join(' · ')
      return '当月售价：' + text
    },
    /**
     * 「废料收入」卡片的悬浮说明（口径③，2026-10-05 重梳理）。
     * 用户原话：「358.9元只是代客户卖废料折现给客户，不是我实际赚的废料钱；
     *           实际赚废料钱是 211.12*(2-1.7)=63.34 元」。
     */
    wasteIncomeTip() {
      return '废料收入 = 卖废料**全额** ' + this.fmtMoney(this.income.waste) + '（废料重量 × 当月实际售价）。\n'
        + '其中**实赚差价** ' + this.fmtMoney(this.income.wasteMargin) + ' = 全额 − 折现给客户 '
        + this.fmtMoney(this.income.wasteFei) + '，这才是真正赚到的废料钱。\n'
        + '业务链条：客户剩板不拉走 → 我方代卖变现 → 按折现单价把钱**折现给客户**（订单实付里已少收这一笔）→ '
        + '我方再按当月实际售价卖掉 ⇒ 差价归我方。\n'
        + '价格来源：点这张卡片可配置**当月各材质桶的实际售价**（碳钢类 / 201 / 304）；\n'
        + '未配价的材质会退回按订单里录的折现单价($s)计价，差价记 0（与旧口径一致）。\n'
        + '⚠️ 看板**不体现**卖剩板时产生的场地占用 / 整理 / 装车等杂费。'
    },
    /** 材料成本卡片提示 */
    materialCostTip() {
      return '材料成本按订单报价记录（realKgPrice / realM2Price）自动复算，口径是**整板采购**：\n'
        + '材料成本 = 整板采购额（Σ 每行 单价 × 用量），**不减**剩板回收。\n'
        + '剩板是「客户不拉走、由我方卖掉变现」的：变现的钱进了我们账户，\n'
        + '但已经在订单金额里抵掉了客户的加工费（订单收入是「客户实付」，少收了那一笔）。\n'
        + '所以这笔变现**不在成本侧冲减**，而是作为「废料收入」加到收入侧 ——\n'
        + '本值 ' + this.fmtMoney(this.ref.materialCost) + ' 是整板全额，'
        + '废料收入 ' + this.fmtMoney(this.income.waste) + ' 在收入侧，两者合并后净利与「材料净额」写法完全等价。\n'
        + '⚠️ 看板**不体现**卖剩板时产生的场地占用 / 整理 / 装车等杂费，只按订单里录入的剩板金额计算，\n'
        + '   故本项略偏乐观（实际到手会少一点）。\n'
        + '自 2026-10-04 起它计入最终净利（此前只作参考值、不计入）。\n'
        + '⚠️ 如果你又在「成本明细」里手工录了一笔钢板采购，会与它重复扣减，注意不要两边都录。'
    },
    /** 净利卡片提示 */
    netProfitTip() {
      return '最终净利 = 总收入（含废料收入） − 直接成本 − 期间费用 − 材料成本（整板） − 订单外协。\n'
        + '其中总收入 ' + this.fmtMoney(this.income.total) + ' = 订单收入 ' + this.fmtMoney(this.income.order)
        + ' ＋ 其他收入 ' + this.fmtMoney(this.income.manual)
        + ' ＋ 废料收入 ' + this.fmtMoney(this.income.waste) + '。\n'
        + '现金净利（只扣手工录入与抄表费用）为 ' + this.fmtMoney(this.profit.cashNet) + '，\n'
        + '再扣材料成本 ' + this.fmtMoney(this.profit.materialCost)
        + ' 与订单外协 ' + this.fmtMoney(this.profit.outsourceCost) + ' 后得到本值。\n'
        + '逐项过程见下方「最终净利计算过程」。'
    },
    meterItems() {
      return (this.summary.costItems || []).filter(item => item.source === 'meter')
    },
    /** 抄表区：在用表具全列出，本月没抄到的显示「未抄表」，方便发现自己漏抄 */
    meterRows() {
      let byMeter = {}
      this.meterItems.forEach(item => {
        byMeter[item.meterId] = item
      })
      return this.activeMeters.map(meter => {
        let item = byMeter[meter.id]
        if (item) return item
        return {
          meterId: meter.id,
          meterName: meter.name,
          previousReading: null,
          currentReading: null,
          usage: null,
          unitName: meter.unitName,
          unitPrice: meter.unitPrice,
          amount: null,
          pending: true
        }
      })
    },
    activeMeters() {
      return this.meters.filter(meter => meter.isActive === 1)
    },
    meterCategories() {
      return this.categories.filter(category => category.isMeter === 1)
    },
    categoryGroups() {
      let groups = []
      let index = {}
      this.categories.forEach(category => {
        if (!index[category.groupKey]) {
          index[category.groupKey] = { groupKey: category.groupKey, groupName: category.groupName, categories: [] }
          groups.push(index[category.groupKey])
        }
        index[category.groupKey].categories.push(category)
      })
      return groups
    },
    isCurrentMonth() {
      let now = new Date()
      return this.month === this.monthOf(now)
    },
    /** 存模板弹窗里的条目合计 */
    saveTemplateTotal() {
      return (this.saveTemplateDialog.items || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
    },
    /** 输入的名称是否已存在同名模板（用于提示「将覆盖」） */
    sameNameTemplate() {
      let name = (this.saveTemplateDialog.name || '').trim()
      if (!name) return null
      return this.templates.find(template => template.name === name) || null
    },
    /** 本月已手工录入的费用，按「类别 + 项目名」建索引，供导入时判重（与后端 expense/copy 口径一致） */
    manualExpenseKeys() {
      let keys = {}
      ;(this.summary.costItems || []).forEach(item => {
        if (item.source !== 'manual') return
        keys[item.categoryId + '|' + String(item.itemName || '').trim()] = true
      })
      return keys
    },
    /** 当前选中的模板名，用于导入完成的提示文案 */
    applyTemplateName() {
      let template = this.templates.find(item => item.id === this.applyDialog.templateId)
      return template ? template.name : ''
    },
    /** 按日明细的每一天 */
    dailyDays() {
      return (this.daily && this.daily.days) || []
    },
    /** 按日明细的合计行 */
    dailyTotal() {
      return (this.daily && this.daily.total) || {}
    }
  },
  methods: {
    // ---------------- 工具 ----------------
    monthOf(date) {
      let month = date.getMonth() + 1
      return date.getFullYear() + '-' + (month < 10 ? '0' + month : '' + month)
    },
    fmtMoney(value) {
      let number = parseFloat(value)
      if (isNaN(number)) return '¥0.00'
      // 负号必须在 ¥ **之前**（`-¥18,668.08`），否则渲染成 `¥-18,668.08` 很难看。
      // 注意 -0 要落回 `¥0.00`，别显示 `-¥0.00`。
      let text = Math.abs(number).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return (number < 0 ? '-¥' : '¥') + text
    },
    fmtNumber(value) {
      let number = parseFloat(value)
      if (isNaN(number)) return '0'
      return number.toFixed(2).replace(/\.00$/, '')
    },
    fmtReading(value) {
      if (value === null || value === undefined || value === '') return '—'
      return this.fmtNumber(value)
    },
    fmtRate(value) {
      let number = parseFloat(value)
      if (isNaN(number)) return '—'
      return (number * 100).toFixed(2) + '%'
    },
    itemTip(item) {
      if (item.source === 'meter') {
        return '读数 ' + this.fmtReading(item.previousReading) + ' → ' + this.fmtReading(item.currentReading) +
          ' × ' + this.fmtNumber(item.unitPrice) + ' 元/' + (item.unitName || '度')
      }
      return item.remark || ''
    },
    // ---------------- 月份 ----------------
    shiftMonth(step) {
      let parts = this.month.split('-')
      let date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1 + step, 1)
      this.month = this.monthOf(date)
      this.loadAll()
    },
    goCurrentMonth() {
      this.month = this.monthOf(new Date())
      this.loadAll()
    },
    // ---------------- 数据加载 ----------------
    loadAll() {
      this.loadSummary()
      this.loadTrend()
      this.loadDaily()
      this.loadMeters()
      this.loadCategories()
    },
    /** 按日明细：订单收入与参考材料成本逐日拆分（用于核对计算过程） */
    loadDaily() {
      // 换月/刷新时清掉已展开的明细缓存，避免显示上个月的旧明细
      this.expandedDay = ''
      this.dailyDetailMap = {}
      this.dailyDetailLoading = {}
      // ⚠️ 本项目的 $get 是「回调式」的：传了第 3 参 suc，返回值就是 undefined（见 main.js 的 _axios），
      //    所以**绝不能**写 `this.$get(...).catch(...)` —— 会抛 TypeError，
      //    而且 loadAll 里 loadDaily 之后的 loadMeters / loadCategories 会被一起拖死。
      //    失败分支一律走第 4 参 err。
      return this.$get('/fin/daily', { month: this.month }, res => {
        this.daily = res || { days: [], total: {} }
      }, () => {
        this.daily = { days: [], total: {} }
      })
    },
    /** 展开/收起某一天的逐单逐行明细（同一天再点一次收起；已拉过的直接用缓存） */
    toggleDayDetail(row) {
      const day = row.day
      if (this.expandedDay === day) {
        this.expandedDay = ''
        return
      }
      this.expandedDay = day
      if (this.dailyDetailMap[day]) return
      this.loadDayDetail(day)
    },
    loadDayDetail(day) {
      this.$set(this.dailyDetailLoading, day, true)
      const done = () => this.$set(this.dailyDetailLoading, day, false)
      // 同上：走第 4 参 err，不要链 .catch
      return this.$get('/fin/daily', { month: this.month, day: day }, res => {
        this.$set(this.dailyDetailMap, day, (res && res.detail) || null)
        done()
      }, () => {
        this.$set(this.dailyDetailMap, day, null)
        done()
      })
    },
    /** 展开视图的三个分组（顺序固定，与后端 detail 的 key 对应） */
    dayDetailGroups(day) {
      const detail = this.dailyDetailMap[day]
      if (!detail) return []
      return [
        { key: 'quote', label: '仅报价（标题含「先报价」）', inStats: false, ...(detail.quote || {}) },
        { key: 'normalOther', label: '参与统计 · 标题以「其他」开头', inStats: true, ...(detail.normalOther || {}) },
        { key: 'normalNonOther', label: '参与统计 · 标题不以「其他」开头', inStats: true, ...(detail.normalNonOther || {}) }
      ].map(group => ({ ...group, summary: group.summary || {} }))
    },
    /**
     * 尺寸字段展示：原始值可能是算式（如 "525/2"、"180*177"）。
     * 是算式就把「原文 = 求值」都显示出来 —— 这是最容易看错的地方，别只显示一个数。
     * ⚠️ 取不到求值（如被跳过的行后端没填 lengthMm）时**只显示原文**，
     *    否则会渲染成「525/2 = 0」，看着像尺寸算错了。
     */
    fmtSizeRaw(raw, value) {
      if (raw === null || raw === undefined || String(raw).trim() === '') return '—'
      const text = String(raw).trim()
      if (/^[+-]?(\d+\.?\d*|\.\d+)$/.test(text)) return text
      if (!isFinite(parseFloat(value))) return text
      return text + ' = ' + this.fmtSizeValue(value)
    },
    fmtSizeValue(value) {
      const number = parseFloat(value)
      if (isNaN(number)) return '0'
      return String(+number.toFixed(4))
    },
    /** 一行报价明细的计算过程（字段与后端 detail.lines[] 一一对应） */
    lineFormula(line) {
      if (line.reason) {
        return '金额按 ¥0 计 —— 跳过原因：' + line.reason
      }
      const area = '面积 = (' + this.fmtSizeValue(line.lengthMm) + '/1000) × (' +
        this.fmtSizeValue(line.widthMm) + '/1000) = ' + (+line.area).toFixed(4) + ' m²'
      const quantity = (+line.quantity).toFixed(2)
      if (String(line.branch || '').indexOf('厚板') === 0) {
        return area + '；板重 = 面积 × m × (t/100) × 1000 = ' + quantity + ' kg' +
          '；金额 = 单价 ' + (+line.unitCost).toFixed(6) + ' 元/kg × ' + quantity + ' = ' + this.fmtMoney(line.amount)
      }
      return area + '；金额 = 单价 ' + (+line.unitCost).toFixed(4) + ' 元/㎡ × ' +
        (+line.area).toFixed(4) + ' = ' + this.fmtMoney(line.amount)
    },
    /** 逐单：整板材料合计 = Σ 行金额（= 该单的材料成本，口径②下两者同值） */
    orderGrossMaterial(order) {
      return (order.lines || []).reduce((sum, line) => sum + (Number(line.amount) || 0), 0)
    },
    /** 逐单：废料收入合计（客户不拉走剩板 ⇒ 我方变现并把钱抵掉他的加工费，计入**收入侧**） */
    orderWasteRecovery(order) {
      return (order.lines || []).reduce((sum, line) => sum + (Number(line.wasteRecovery) || 0), 0)
    },
    /**
     * 逐单材料成本的口径备注（挂在「材料成本」上的 title）。
     * ⚠️ 别再把这句写回「参考值 / 只作参考」—— 2026-10-04 起材料成本是**整板采购**且**计入净利**；
     *    也别写回「材料成本 − 剩板回收」 —— 剩板回收现在走**收入侧**（见 orderWasteRecovery / 废料收入）。
     */
    orderMaterialTip(order) {
      const gross = this.orderGrossMaterial(order)
      const recovery = this.orderWasteRecovery(order)
      if (recovery > 0) {
        return '材料成本（整板采购）= Σ 行金额 = ' + this.fmtMoney(order.materialCost) + '（本单 ' + gross.toFixed(2) + '）。\n' +
          '本单另有**废料收入** ' + this.fmtMoney(recovery) + '：剩板客户不拉走，由我方变现并把钱直接抵掉他的加工费，\n' +
          '所以「订单金额（客户实付）」里已经少收了这笔 ⇒ 它在**收入侧**加回，不在成本侧冲减。'
      }
      return '材料成本（整板采购）按报价明细（realKgPrice / realM2Price）复算，本单无剩板回收 ⇒ 无废料收入。'
    },
    loadSummary() {
      this.$get('/fin/summary', { month: this.month }, res => {
        this.summary = res || {}
        this.groups = (res && res.groups) || []
      })
    },
    loadTrend() {
      this.$get('/fin/trend', { months: 12, endMonth: this.month }, res => {
        this.trend = res || []
        this.renderTrend()
      })
    },
    loadMeters() {
      return this.$get('/fin/meter/list', {}).then(res => {
        this.meters = res || []
      }).catch(() => {
        this.meters = []
      })
    },
    loadCategories() {
      return this.$get('/fin/category/list', { onlyActive: 1 }).then(res => {
        this.categories = res || []
      }).catch(() => {
        this.categories = []
      })
    },
    renderTrend() {
      if (!this.$refs.trendChart) return
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
      this.chart = echarts.init(this.$refs.trendChart)
      let months = this.trend.map(item => item.month)
      let incomes = this.trend.map(item => (item.income || {}).total || 0)
      let gross = this.trend.map(item => (item.profit || {}).gross || 0)
      let net = this.trend.map(item => (item.profit || {}).net || 0)
      this.chart.setOption({
        grid: { left: 8, right: 12, top: 34, bottom: 4, containLabel: true },
        tooltip: { trigger: 'axis' },
        legend: { data: ['总收入', '毛利', '净利'], top: 0, itemWidth: 12, itemHeight: 8 },
        xAxis: { type: 'category', data: months, axisLine: { lineStyle: { color: '#dcdce0' } } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f0f2f5' } } },
        series: [
          { name: '总收入', type: 'bar', barMaxWidth: 22, data: incomes, itemStyle: { color: '#a3aec2' } },
          { name: '毛利', type: 'line', smooth: true, data: gross, itemStyle: { color: '#02875b' } },
          { name: '净利', type: 'line', smooth: true, data: net, itemStyle: { color: '#505f79' } }
        ]
      })
    },
    // ---------------- 费用 ----------------
    /**
     * 这笔手工费用是否勾了「外协」。
     * 后端给的是 Integer 0/1，但老接口 / 手工造的 fixture 可能给 true / '1' / undefined，
     * 所以统一用 Number() 归一（Number(null)=0、Number(undefined)=NaN ⇒ 都判否）。
     * ⚠️ 不能写成裸 `v-if="item.isOutsource"` —— 那样字符串 "0" 会被当成真值，标签会误显示。
     */
    isOutsourceItem(item) {
      return Number(item && item.isOutsource) === 1
    },
    openExpenseDialog(item, group) {
      if (item) {
        this.expenseDialog.form = {
          id: item.id,
          month: this.month,
          categoryId: item.categoryId,
          itemName: item.itemName,
          amount: item.amount,
          // 后端返回 0/1；老数据可能没有该字段，用 || 兜底成 0（非外协）
          isOutsource: item.isOutsource ? 1 : 0,
          remark: item.remark
        }
      } else {
        let defaultCategory = null
        if (group) {
          let first = this.categories.find(category => category.groupKey === group.groupKey)
          defaultCategory = first ? first.id : null
        }
        this.expenseDialog.form = {
          id: null,
          month: this.month,
          categoryId: defaultCategory,
          itemName: '',
          amount: null,
          isOutsource: 0,
          remark: ''
        }
      }
      this.expenseDialog.show = true
    },
    saveExpense() {
      let form = this.expenseDialog.form
      if (!form.categoryId) {
        this.$msg('请选择费用类别', 'error')
        return
      }
      if (form.amount === null || form.amount === '' || isNaN(parseFloat(form.amount))) {
        this.$msg('请填写金额', 'error')
        return
      }
      this.$post('/fin/expense/save', {
        id: form.id,
        month: form.month,
        categoryId: form.categoryId,
        itemName: form.itemName,
        amount: parseFloat(form.amount),
        // 0/1 用数字传，后端是 Integer；不能传 true/false（fastjson 也能转，但保持类型稳定）
        isOutsource: form.isOutsource ? 1 : 0,
        remark: form.remark
      }, () => {
        this.expenseDialog.show = false
        this.loadSummary()
        this.loadTrend()
      })
    },
    removeExpense(item) {
      if (!window.confirm('确认删除「' + item.itemName + '」这笔费用？')) return
      this.$post('/fin/expense/delete?id=' + item.id, {}, () => {
        this.loadSummary()
        this.loadTrend()
      })
    },
    copyLastMonthExpense() {
      let parts = this.month.split('-')
      let last = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 2, 1)
      let fromMonth = this.monthOf(last)
      this.$post('/fin/expense/copy?fromMonth=' + fromMonth + '&toMonth=' + this.month, {}, () => {
        this.loadSummary()
        this.loadTrend()
      })
    },
    // ---------------- 费用模板（存为模板 / 从模板导入） ----------------
    loadTemplates() {
      try {
        let raw = window.localStorage.getItem(TEMPLATE_STORE_KEY)
        let list = raw ? JSON.parse(raw) : []
        this.templates = Array.isArray(list) ? list : []
      } catch (error) {
        console.error('读取费用模板失败', error)
        this.templates = []
      }
    },
    persistTemplates() {
      try {
        window.localStorage.setItem(TEMPLATE_STORE_KEY, JSON.stringify(this.templates))
        return true
      } catch (error) {
        console.error('写入费用模板失败', error)
        this.$msg('模板保存失败：浏览器本地存储不可用（无痕模式或已满）', 'error')
        return false
      }
    },
    openSaveTemplateDialog() {
      let items = (this.summary.costItems || [])
        .filter(item => item.source === 'manual')
        .map(item => ({
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          itemName: item.itemName,
          amount: item.amount,
          // 「外协」是这笔费用的固有性质，存模板时要一起带上（否则导入后标记丢失）
          isOutsource: item.isOutsource ? 1 : 0,
          remark: item.remark || ''
        }))
      if (!items.length) {
        this.$msg('本月还没有手工录入的费用，没有可保存的内容', 'error')
        return
      }
      this.saveTemplateDialog.items = items
      this.saveTemplateDialog.name = this.month + ' 月度费用'
      this.saveTemplateDialog.show = true
    },
    confirmSaveTemplate() {
      let name = (this.saveTemplateDialog.name || '').trim()
      if (!name) {
        this.$msg('请填写模板名称', 'error')
        return
      }
      let snapshot = {
        id: 'tpl-' + Date.now(),
        name: name,
        sourceMonth: this.month,
        createdAt: this.stampOf(new Date()),
        items: this.saveTemplateDialog.items
      }
      let existIndex = this.templates.findIndex(template => template.name === name)
      if (existIndex >= 0) {
        if (!window.confirm('已存在同名模板「' + name + '」，用本月的 ' + snapshot.items.length + ' 条覆盖它？')) return
        snapshot.id = this.templates[existIndex].id
        this.templates.splice(existIndex, 1, snapshot)
      } else {
        if (this.templates.length >= TEMPLATE_MAX) {
          this.$msg('模板最多存 ' + TEMPLATE_MAX + ' 个，请先在「从模板导入」里删掉不用的', 'error')
          return
        }
        this.templates.push(snapshot)
      }
      if (!this.persistTemplates()) return
      this.saveTemplateDialog.show = false
      this.$msg('已保存模板「' + name + '」（' + snapshot.items.length + ' 条）')
    },
    openApplyDialog() {
      this.loadTemplates()
      if (!this.templates.length) {
        this.$msg('还没有费用模板：先在一个录好费用的月份点「存为模板」', 'error')
        return
      }
      this.applyDialog.templateId = this.templates[0].id
      this.applyDialog.show = true
      this.refreshApplyPreview()
    },
    /** 算出「将新增 / 将跳过」两类条目，导入前先让用户看清楚会做什么 */
    refreshApplyPreview() {
      let template = this.templates.find(item => item.id === this.applyDialog.templateId)
      if (!template) {
        this.applyDialog.rows = []
        this.applyDialog.toAdd = []
        this.applyDialog.skipped = []
        return
      }
      let existKeys = this.manualExpenseKeys
      let rows = (template.items || []).map(item => {
        let skip = !!existKeys[item.categoryId + '|' + String(item.itemName || '').trim()]
        return Object.assign({}, item, { skip: skip })
      })
      this.applyDialog.rows = rows
      this.applyDialog.toAdd = rows.filter(row => !row.skip)
      this.applyDialog.skipped = rows.filter(row => row.skip)
    },
    confirmApplyTemplate() {
      let toAdd = this.applyDialog.toAdd
      if (!toAdd.length) {
        this.$msg('本月已包含模板里的全部条目，没有需要导入的', 'error')
        return Promise.resolve(0)
      }
      let templateName = this.applyTemplateName
      let skippedCount = this.applyDialog.skipped.length
      this.applyDialog.busy = true
      // 逐条串行保存：不并发打后端，中途失败也能定位到具体是哪个条目
      let chain = Promise.resolve()
      let failed = 0
      toAdd.forEach(row => {
        chain = chain
          .then(() => this.$axios.post('/fin/expense/save', {
            id: null,
            month: this.month,
            categoryId: row.categoryId,
            itemName: row.itemName,
            amount: parseFloat(row.amount),
            // 模板里的「外协」标记要原样带过去（老模板没有该字段 ⇒ 0）
            isOutsource: row.isOutsource ? 1 : 0,
            remark: row.remark || ''
          }))
          .then(res => {
            let body = res && res.data
            if (!body || !body.code) {
              failed++
              console.error('导入费用失败', row, body)
            }
          })
          .catch(error => {
            failed++
            console.error('导入费用失败', row, error)
          })
      })
      let finished = chain.then(() => {
        this.applyDialog.busy = false
        this.applyDialog.show = false
        this.loadSummary()
        this.loadTrend()
        let done = toAdd.length - failed
        let tail = '（跳过 ' + skippedCount + ' 条已存在的）'
        if (failed) {
          this.$msg('导入完成：成功 ' + done + ' 条，失败 ' + failed + ' 条' + tail, 'error')
        } else {
          this.$msg('已从模板「' + templateName + '」导入 ' + done + ' 条' + tail)
        }
      })
      return finished
    },
    removeTemplate(template) {
      if (!window.confirm('删除模板「' + template.name + '」？已录入的费用不受影响。')) return
      this.templates = this.templates.filter(item => item.id !== template.id)
      if (!this.persistTemplates()) return
      if (this.applyDialog.templateId === template.id) {
        this.applyDialog.templateId = this.templates.length ? this.templates[0].id : null
        this.refreshApplyPreview()
      }
      this.$msg('已删除模板「' + template.name + '」')
    },
    // ---------------- 其他收入 ----------------
    openIncomeDialog(item) {
      this.incomeDialog.form = item
        ? { id: item.id, month: this.month, itemName: item.itemName, amount: item.amount, remark: item.remark }
        : { id: null, month: this.month, itemName: '', amount: null, remark: '' }
      this.incomeDialog.show = true
    },
    saveIncome() {
      let form = this.incomeDialog.form
      if (form.amount === null || form.amount === '' || isNaN(parseFloat(form.amount))) {
        this.$msg('请填写金额', 'error')
        return
      }
      this.$post('/fin/income/save', {
        id: form.id,
        month: form.month,
        itemName: form.itemName,
        amount: parseFloat(form.amount),
        remark: form.remark
      }, () => {
        this.incomeDialog.show = false
        this.loadSummary()
        this.loadTrend()
      })
    },
    removeIncome(item) {
      if (!window.confirm('确认删除「' + item.itemName + '」？')) return
      this.$post('/fin/income/delete?id=' + item.id, {}, () => {
        this.loadSummary()
        this.loadTrend()
      })
    },
    // ---------------- 口径③：当月废料实际售价 ----------------
    /**
     * 打开「当月废料实际售价」配置。回显当前 summary.income.wastePrices（空对象 = 未配价）。
     */
    openWastePriceDialog() {
      const configured = this.income.wastePrices || {}
      const prices = {}
      this.wasteBuckets.forEach(bucket => {
        const price = configured[bucket.key]
        prices[bucket.key] = (price !== null && price !== undefined && Number(price) > 0) ? Number(price) : null
      })
      this.wastePriceDialog = { show: true, month: this.month, prices: prices, saving: false }
    },
    /**
     * 保存当月废料售价：按材质桶逐个提交（后端按「月份+桶」覆盖；单价留空 ⇒ 清除该桶配置）。
     * 用回调串行而不是并发，避免 3 个写请求同时打库。
     */
    saveWastePrice() {
      const toPrice = value => {
        if (value === null || value === undefined || value === '') return null
        const num = Number(value)
        return (isFinite(num) && num >= 0) ? num : null
      }
      const jobs = this.wasteBuckets.map(bucket => ({
        bucket: bucket.key,
        unitPrice: toPrice(this.wastePriceDialog.prices[bucket.key])
      }))
      this.wastePriceDialog.saving = true
      const next = index => {
        if (index >= jobs.length) {
          this.wastePriceDialog.saving = false
          this.wastePriceDialog.show = false
          this.$msg('已保存当月废料售价', 'success')
          this.loadSummary()
          this.loadTrend()
          return
        }
        const job = jobs[index]
        this.$post('/fin/waste-price/save', {
          month: this.month,
          bucket: job.bucket,
          unitPrice: job.unitPrice
        }, () => next(index + 1), () => {
          this.wastePriceDialog.saving = false
          this.$msg('保存失败，请重试', 'error')
        })
      }
      next(0)
    },
    // ---------------- 抄表 ----------------
    openReadingDialog(item) {
      let parts = this.month.split('-')
      let year = parseInt(parts[0], 10)
      let monthIndex = parseInt(parts[1], 10) - 1
      let today = new Date()
      let defaultDay = (today.getFullYear() === year && today.getMonth() === monthIndex)
        ? this.dayString(today)
        : this.dayString(new Date(year, monthIndex + 1, 0))
      this.readingDialog.lastReading = null
      this.readingDialog.form = {
        meterId: item ? item.meterId : null,
        readDate: defaultDay,
        reading: null,
        remark: ''
      }
      this.readingDialog.show = true
      if (item) this.loadLastReading()
    },
    dayString(date) {
      let month = date.getMonth() + 1
      let day = date.getDate()
      return date.getFullYear() + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day)
    },
    /** 生成本地时间戳字符串，用于模板的「保存时间」 */
    stampOf(date) {
      let pad = value => (value < 10 ? '0' + value : '' + value)
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) +
        ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
    },
    loadLastReading() {
      let meterId = this.readingDialog.form.meterId
      this.readingDialog.lastReading = null
      if (!meterId) return
      this.$get('/fin/reading/last', { meterId: meterId }, res => {
        this.readingDialog.lastReading = res || null
      })
    },
    saveReading() {
      let form = this.readingDialog.form
      if (!form.meterId) {
        this.$msg('请选择表具', 'error')
        return
      }
      if (!form.readDate) {
        this.$msg('请选择抄表日期', 'error')
        return
      }
      if (form.reading === null || form.reading === '' || isNaN(parseFloat(form.reading))) {
        this.$msg('请填写抄见读数', 'error')
        return
      }
      this.$post('/fin/reading/save', {
        meterId: form.meterId,
        readDate: form.readDate,
        reading: parseFloat(form.reading),
        remark: form.remark
      }, () => {
        this.readingDialog.show = false
        this.loadSummary()
        this.loadTrend()
      })
    },
    // ---------------- 表具 ----------------
    openMeterDialog() {
      this.meterDialog.show = true
      return Promise.all([this.loadMeters(), this.loadCategories()])
    },
    addMeter() {
      let meter = {
        id: null,
        name: '',
        meterType: 'electric',
        categoryId: null,
        unitPrice: 1.5,
        unitName: '度',
        baseReading: null,
        sortNo: this.meters.length * 10,
        isActive: 1
      }
      // 类别已加载时顺手把「电费」填上，避免新增行忘了选类别导致费用落不进直/间分类
      let hit = this.meterCategories.find(category => category.name === '电费')
      if (hit) meter.categoryId = hit.id
      this.meters.push(meter)
    },
    saveMeter(meter) {
      if (!meter.name) {
        this.$msg('表具名称不能为空', 'error')
        return
      }
      this.$post('/fin/meter/save', meter, () => {
        this.loadMeters()
        this.loadSummary()
      })
    },
    saveAllMeters() {
      // 注意：这里必须包含「刚新增、还没有 id」的行，否则点了「全部保存」等于什么都没存
      let rows = this.meters.filter(meter => meter && String(meter.name || '').trim() !== '')
      if (!rows.length) {
        this.$msg('没有可保存的表具：请先点「新增表具」并填写名称', 'error')
        return
      }
      rows.forEach(meter => {
        this.$post('/fin/meter/save', meter, () => {
          this.loadMeters()
          this.loadSummary()
        })
      })
    },
    removeMeter(meter) {
      // 还没存过的新行：直接本地移除，不要拿 id=null 去调删除接口
      if (meter.id === null || meter.id === undefined) {
        this.meters = this.meters.filter(item => item !== meter)
        return
      }
      if (!window.confirm('删除表具「' + meter.name + '」会同时删除它的全部抄表记录，确认？')) return
      this.$post('/fin/meter/delete?id=' + meter.id, {}, () => {
        this.loadMeters()
        this.loadSummary()
      })
    },
    /** 切换表具类型时联动单位与归属类别，省得手填漏填 */
    onMeterTypeChange(meter) {
      if (meter.meterType === 'water') {
        meter.unitName = '吨'
      } else if (meter.meterType === 'electric') {
        meter.unitName = '度'
      }
      let wantedName = meter.meterType === 'water' ? '水费' : (meter.meterType === 'electric' ? '电费' : '')
      if (!wantedName) return
      let hit = this.meterCategories.find(category => category.name === wantedName)
      if (hit) meter.categoryId = hit.id
    },
    /** 从「抄表」空态直接跳到新增表具 */
    gotoAddMeter() {
      this.readingDialog.show = false
      this.openMeterDialog().then(() => {
        this.addMeter()
      })
    },
    /** 抄表录入弹窗 → 表具管理（常驻入口，避免加完表后找不到表具管理） */
    gotoMeterDialog() {
      this.readingDialog.show = false
      this.openMeterDialog()
    },
    /** 表具管理 → 抄表录入 */
    gotoReadingDialog() {
      this.meterDialog.show = false
      this.openReadingDialog(null)
    }
  },
  mounted() {
    this.month = this.monthOf(new Date())
    this.loadTemplates()
    this.loadAll()
    window.addEventListener('resize', this.renderTrend)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.renderTrend)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  }
}
</script>

<style scoped>
.fin-page {
  padding: 16px;
  background-color: #f4f6fa;
  min-height: 100vh;
  font-size: 13px;
  color: #2c3e50;
}

/* ---------- 顶部 ---------- */
.fin-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.fin-title h2 {
  font-size: 19px;
  font-weight: 600;
  margin: 0;
}

.fin-sub {
  margin: 4px 0 0;
  font-size: 12px;
  color: #8898a8;
}

.fin-month {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.fin-month-text {
  font-size: 16px;
  font-weight: 600;
  min-width: 84px;
  text-align: center;
}

.fin-actions {
  display: flex;
  gap: 8px;
}

/* ---------- 按钮 ---------- */
.fin-btn {
  border: 1px solid #505f79;
  background-color: #505f79;
  color: #fff;
  border-radius: 3px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: opacity .2s;
}

.fin-btn:hover {
  opacity: .86;
}

.fin-btn:disabled {
  opacity: .4;
  cursor: not-allowed;
}

.fin-btn-ghost {
  background-color: #fff;
  color: #505f79;
  border-color: #d6dbe4;
}

.fin-btn-mini {
  padding: 3px 10px;
  font-size: 12px;
}

/* ---------- 指标卡 ---------- */
.fin-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.fin-card {
  background-color: #fff;
  border-radius: 4px;
  padding: 12px 14px;
  box-shadow: rgba(0, 0, 0, .05) 0 1px 0 0, rgba(0, 0, 0, .03) 0 0 0 1px;
}

.fin-card-label {
  margin: 0;
  font-size: 12px;
  color: #8898a8;
}

.fin-card-value {
  margin: 6px 0 2px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
}

.fin-card-tip {
  margin: 0;
  font-size: 11px;
  color: #a3aec2;
}

.fin-card-strong .fin-card-value {
  color: #505f79;
}

/* ---------- 两栏 ---------- */
.fin-grid {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.fin-col-main {
  flex: 1 1 560px;
  min-width: 320px;
}

.fin-col-side {
  flex: 0 1 420px;
  min-width: 300px;
}

.fin-section {
  background-color: #fff;
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 12px;
  box-shadow: rgba(0, 0, 0, .05) 0 1px 0 0, rgba(0, 0, 0, .03) 0 0 0 1px;
}

.fin-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.fin-section-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.fin-section-total {
  font-size: 13px;
  font-weight: 600;
  color: #505f79;
}

/* 分区头右侧操作组（合计 + 若干小按钮） */
.fin-head-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ---------- 表格 ---------- */
.fin-table {
  width: 100%;
  border-collapse: collapse;
}

.fin-table th {
  text-align: left;
  font-weight: 500;
  font-size: 12px;
  color: #8898a8;
  padding: 6px 6px;
  border-bottom: 1px solid #eceef2;
  white-space: nowrap;
}

.fin-table td {
  padding: 7px 6px;
  border-bottom: 1px solid #f4f6fa;
  vertical-align: middle;
}

.fin-table tbody tr:last-child td {
  border-bottom: none;
}

.fin-table-sm th,
.fin-table-sm td {
  padding: 5px 6px;
  font-size: 12px;
}

/* 「最终净利计算过程」里的中间小计行（现金净利 / 最终净利） */
.fin-row-subtotal td {
  border-top: 1px dashed #e3e8ee;
  background-color: #f7f9fc;
}

.fin-t-right {
  text-align: right;
}

.fin-num {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.fin-muted {
  color: #8898a8;
}

/* 负数（如参考毛利为负）用红色，和国内「红涨绿跌」的语义区分开：这里只是提示异常 */
.fin-neg {
  color: #c0392b;
}

.fin-table tfoot td {
  font-weight: 600;
  background-color: #f7fafd;
  border-top: 1px solid #e3eaf3;
}

.fin-small {
  font-size: 12px;
}

.fin-empty {
  color: #a3aec2;
  padding: 10px 6px;
  font-size: 12px;
}

.fin-row-pending td {
  color: #b9c2cf;
}

/* ---------- 分组 ---------- */
.fin-group {
  border: 1px solid #eceef2;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 10px;
}

.fin-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.fin-group-name {
  font-weight: 600;
  font-size: 13px;
}

.fin-group-badge {
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 9px;
}

.fin-badge-direct {
  background-color: #fdecea;
  color: #c0392b;
}

.fin-badge-period {
  background-color: #eef1f6;
  color: #505f79;
}

.fin-group-total {
  margin-left: auto;
  font-weight: 600;
  color: #505f79;
}

.fin-group-foot {
  margin-top: 6px;
}

/* ---------- 标签 / 链接 ---------- */
.fin-tag {
  display: inline-block;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 9px;
  white-space: nowrap;
}

.fin-tag-auto {
  background-color: #e6f6ee;
  color: #02875b;
}

.fin-tag-manual {
  background-color: #eef1f6;
  color: #505f79;
}

/* 外协标记：橙色系，与「自动(绿)/手工(灰)」的来源标签区分开 —— 它是费用性质，不是来源 */
.fin-tag-outsource {
  margin-left: 4px;
  background-color: #fdf3e3;
  color: #d48806;
  cursor: help;
}

.fin-link {
  color: #1a73e8;
  cursor: pointer;
  margin-left: 6px;
}

.fin-link:hover {
  text-decoration: underline;
}

.fin-link-danger {
  color: #ea4545;
}

.fin-chart {
  width: 100%;
  height: 220px;
}

.fin-note {
  margin: 8px 0 0;
  font-size: 11px;
  color: #a3aec2;
  line-height: 1.6;
}

/* ---------- 弹窗 ---------- */
.fin-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, .35);
  z-index: 3000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 16px 16px;
  overflow: auto;
}

.fin-dialog {
  background-color: #fff;
  border-radius: 5px;
  width: 440px;
  max-width: 100%;
  box-shadow: 0 8px 30px rgba(0, 0, 0, .2);
}

.fin-dialog-wide {
  width: 900px;
}

.fin-dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eceef2;
}

.fin-dialog-head h3 {
  margin: 0;
  font-size: 15px;
}

.fin-dialog-close {
  cursor: pointer;
  color: #8898a8;
}

.fin-dialog-body {
  padding: 14px 16px;
}

.fin-dialog-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eceef2;
}

.fin-field {
  margin-bottom: 12px;
}

.fin-field label {
  display: block;
  font-size: 12px;
  color: #8898a8;
  margin-bottom: 4px;
}

.fin-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d6dbe4;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 13px;
  color: #2c3e50;
  outline: none;
  background-color: #fff;
}

.fin-input:focus {
  border-color: #505f79;
}

.fin-input-mini {
  padding: 3px 5px;
  font-size: 12px;
}

.fin-hint {
  margin: 6px 0 0;
  font-size: 11px;
  color: #a3aec2;
  line-height: 1.6;
}

.fin-hint-warn {
  color: #c0392b;
}

/* 勾选框行（外协）：与上面「费用类别 / 明细名」等输入行视觉上区分开 */
.fin-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #2c3e50;
  cursor: pointer;
  user-select: none;
}

.fin-check input[type="checkbox"] {
  width: 14px;
  height: 14px;
  margin: 0;
  cursor: pointer;
}

/* ---------------- 按日明细：「明细」展开 ---------------- */
.fin-detail-toggle {
  padding: 1px 6px;
  font-size: 11px;
  line-height: 1.5;
}

.fin-detail-toggle-on {
  background-color: #e8f0fa;
  border-color: #b8cfe8;
  color: #2c6cb0;
}

/* 展开行：整行浅底，和上面的日期行区分开 */
.fin-detail-row > td {
  background-color: #fbfcfe;
  border-top: none;
  padding: 10px 12px 14px;
}

.fin-detail-sum {
  margin-bottom: 10px;
}

/* 被规则 1 排除的报价单分组：整行灰掉，但仍要看得清数字 */
.fin-detail-excluded td {
  color: #a3aec2;
}

.fin-detail-stat td {
  border-top: 2px solid #d7e3f0;
}

.fin-detail-group {
  margin-top: 12px;
}

.fin-detail-group-title {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #4a5a6e;
}

.fin-detail-order {
  border: 1px solid #e9eff6;
  border-radius: 4px;
  background-color: #ffffff;
  padding: 7px 10px;
  margin-bottom: 6px;
}

.fin-detail-order-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
}

.fin-detail-oid {
  font-weight: 700;
  color: #2c3e50;
}

.fin-detail-cust {
  color: #2c3e50;
}

.fin-detail-title {
  max-width: 42%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fin-detail-badge {
  padding: 0 6px;
  border-radius: 8px;
  font-size: 11px;
  background-color: #eef2f7;
}

.fin-detail-priced {
  color: #1e7f4f;
}

.fin-detail-unpriced {
  color: #c9760a;
}

.fin-detail-figs {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 4px;
  font-size: 12px;
  color: #4a5a6e;
}

.fin-detail-lines {
  margin-top: 6px;
}

.fin-detail-lines th,
.fin-detail-lines td {
  font-size: 11.5px;
  padding: 4px 6px;
}

.fin-detail-params span {
  margin-right: 10px;
  white-space: nowrap;
}

.fin-detail-params code {
  background-color: #f2f5f9;
  padding: 0 3px;
  border-radius: 2px;
  font-family: Consolas, Monaco, monospace;
}

.fin-detail-baoliao-false {
  color: #c0392b;
  font-weight: 600;
}

.fin-detail-formula {
  font-family: Consolas, Monaco, monospace;
  font-size: 11.5px;
  color: #46586d;
  line-height: 1.6;
  word-break: break-all;
}

/* 被跳过的明细行（客户来料 / 尺寸缺失 / 单价缺失）：底色提示，金额按 0 计 */
.fin-detail-skipped td {
  background-color: #fffaf3;
}
</style>
